import React, { useState, useEffect, useCallback } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Sheet, Loader2, Search, ExternalLink, Rows } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { useNavigate } from 'react-router-dom';
import { sheetsService, SchemaColumn, SearchCriteria } from '../../../services/sheetsService';
import { s3Config } from '../../../aws-config';
import { useUpstreamFile } from '../useUpstreamFile';

export const SheetsNode: React.FC<{ id: string; data: { label: string; color: string; demoState?: { schema: SchemaColumn[]; rows: Record<string, any>[]; rowCount: number } } }> = ({ id, data }) => {
  const navigate = useNavigate();
  const selectedFile = useUpstreamFile(id);
  const hasDemoData = !!data.demoState;
  const hasFile = !!selectedFile || hasDemoData;
  const [schema, setSchema] = useState<SchemaColumn[]>([]);
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [filterCol, setFilterCol] = useState('');
  const [filterOp, setFilterOp] = useState<SearchCriteria['operator']>('contains');
  const [filterVal, setFilterVal] = useState('');

  const s3Key = selectedFile?.s3Key || `users/placeholder/${selectedFile?.fileName}`;
  const bucket = s3Config.primaryDataBucket;

  const loadData = useCallback(async () => {
    if (!selectedFile) return;
    setLoading(true);
    setMatchCount(null);
    try {
      // Fetch schema first to find a string column for the match-all query
      const schemaRes = await sheetsService.getSchema(bucket, s3Key, { isMultipart: selectedFile.isMultipart });
      if (schemaRes.success && schemaRes.schema) {
        setSchema(schemaRes.schema);
        setRowCount(schemaRes.row_count || 0);

        // Pick a string/text column for the contains '' query (numeric columns return 0 rows)
        // Rust worker returns parquet physical types: BYTE_ARRAY for strings, DOUBLE/INT64 etc for numbers
        const textCol = schemaRes.schema.find(
          (c) => c.data_type === 'BYTE_ARRAY' || c.data_type === 'Utf8' || c.data_type === 'LargeUtf8' || c.data_type.includes('Utf8')
        ) || schemaRes.schema[0];

        const dataRes = await sheetsService.search(
          bucket,
          s3Key,
          { column: textCol.name, operator: 'contains', value: '' },
          { limit: 20, offset: 0, isMultipart: selectedFile.isMultipart }
        );
        if (dataRes.success && dataRes.data) {
          setRows(dataRes.data);
        }
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [selectedFile, bucket, s3Key]);

  useEffect(() => {
    if (data.demoState) {
      setSchema(data.demoState.schema);
      setRows(data.demoState.rows);
      setRowCount(data.demoState.rowCount);
      setLoading(false);
      return;
    }
    if (selectedFile) {
      loadData();
      setFilterCol('');
      setFilterVal('');
    } else {
      setSchema([]);
      setRows([]);
      setRowCount(0);
    }
  }, [selectedFile, loadData, data.demoState]);

  const handleFilter = async () => {
    if (!selectedFile || !filterCol || !filterVal) return;
    setLoading(true);
    try {
      const res = await sheetsService.search(
        bucket,
        s3Key,
        { column: filterCol, operator: filterOp, value: filterVal },
        { limit: 20, offset: 0, isMultipart: selectedFile.isMultipart }
      );
      if (res.success && res.data) {
        setRows(res.data);
        setMatchCount(res.matched_rows ?? res.data.length);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const displayCols = schema.slice(0, 5);

  return (
    <div className="bg-card border rounded-xl shadow-md w-[450px] overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
        style={{ background: data.color }}
      >
        <Sheet className="h-3.5 w-3.5" />
        {data.label}
        {rowCount > 0 && (
          <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0 bg-white/20 text-white">
            <Rows className="h-2.5 w-2.5 mr-0.5" />
            {rowCount.toLocaleString()} rows
          </Badge>
        )}
      </div>

      <div className="p-3 space-y-2">
        {!hasFile ? (
          <p className="text-[11px] text-muted-foreground text-center py-2">
            Connect a data source
          </p>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded-md px-2 py-1">
            <span className="truncate">{selectedFile?.fileName || 'Demo Dataset'}</span>
          </div>
        )}

        {hasFile && loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {hasFile && !loading && schema.length > 0 && (
          <>
            {/* Filter bar */}
            <div className="flex items-center gap-1 nodrag">
              <Select value={filterCol} onValueChange={setFilterCol}>
                <SelectTrigger className="h-7 text-[11px] w-[120px]">
                  <SelectValue placeholder="Column" />
                </SelectTrigger>
                <SelectContent>
                  {schema.map((c) => (
                    <SelectItem key={c.name} value={c.name} className="text-xs">
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterOp} onValueChange={(v) => setFilterOp(v as SearchCriteria['operator'])}>
                <SelectTrigger className="h-7 text-[11px] w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(['contains', 'eq', 'ne', 'gt', 'lt', 'starts_with', 'ends_with'] as const).map((op) => (
                    <SelectItem key={op} value={op} className="text-xs">
                      {op}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="h-7 text-[11px] flex-1"
                placeholder="Value..."
                value={filterVal}
                onChange={(e) => setFilterVal(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
              />
              <Button
                variant="secondary"
                size="sm"
                className="h-7 text-[11px] px-2"
                onClick={handleFilter}
                disabled={!filterCol || !filterVal}
              >
                <Search className="h-3 w-3" />
              </Button>
            </div>

            {matchCount !== null && (
              <Badge variant="outline" className="text-[10px]">
                {matchCount.toLocaleString()} matches
              </Badge>
            )}

            {/* Mini table */}
            <div className="overflow-auto min-h-[160px] max-h-[280px] border rounded text-[11px] nodrag nowheel">
              <table className="w-full">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    {displayCols.map((c) => (
                      <th key={c.name} className="text-left px-2 py-1 font-medium whitespace-nowrap">
                        {c.name}
                      </th>
                    ))}
                    {schema.length > 5 && (
                      <th className="px-2 py-1 text-muted-foreground">+{schema.length - 5}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 20).map((row, i) => (
                    <tr key={i} className="border-t">
                      {displayCols.map((c) => (
                        <td key={c.name} className="px-2 py-1 truncate max-w-[100px]">
                          {row[c.name] != null ? String(row[c.name]) : ''}
                        </td>
                      ))}
                      {schema.length > 5 && <td className="px-2 py-1 text-muted-foreground">...</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Button
              variant="link"
              size="sm"
              className="text-[11px] p-0 h-auto"
              onClick={() =>
                navigate('/sheets', {
                  state: { preselectedFile: selectedFile?.fileName },
                })
              }
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Open Full View
            </Button>
          </>
        )}
      </div>

      <Handle type="target" position={Position.Left} className="!bg-muted-foreground" />
      <Handle type="source" position={Position.Right} className="!bg-muted-foreground" />
    </div>
  );
};
